import { useEffect, useState } from 'react'
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface AlarmPayload {
  id: number
  severity: string  // info | warning | critical
  title: string
  body: string
  expires_at?: number
  created_at?: number
}

const SEVERITY_META: Record<string, { icon: any; cls: string; label: string }> = {
  info:     { icon: Info,           cls: 'bg-primary/10 text-primary border-primary/30',                     label: 'Информация' },
  warning:  { icon: AlertTriangle,  cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30',               label: 'Внимание' },
  critical: { icon: AlertCircle,    cls: 'bg-red-500/10 text-red-400 border-red-500/30',                     label: 'Срочно' },
}

interface AlarmModalProps {
  alarm: AlarmPayload
  onDismiss: () => void
}

export default function AlarmModal({ alarm, onDismiss }: AlarmModalProps) {
  const [busy, setBusy] = useState(false)
  const meta = SEVERITY_META[alarm.severity] || SEVERITY_META.info
  const Icon = meta.icon

  // Block scroll behind the modal while it's visible.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  const handleDismiss = async () => {
    if (busy) return
    setBusy(true)
    try {
      // The backend will POST to the license server and clear the
      // cached alarm; even if the network call fails we still close
      // the modal locally so the user is not stuck.
      await fetch('/api/alarms/dismiss', { method: 'POST' })
    } catch { /* noop */ }
    onDismiss()
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className={`relative w-full max-w-lg rounded-2xl border ${meta.cls} bg-card shadow-2xl overflow-hidden`}>
        {/* Severity strip on the left edge */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${
          alarm.severity === 'critical' ? 'bg-red-500' :
          alarm.severity === 'warning' ? 'bg-amber-500' : 'bg-primary'
        }`} />

        <div className="p-6 pl-7 space-y-4">
          <div className="flex items-start gap-3">
            <div className={`shrink-0 p-2 rounded-full ${meta.cls}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                {meta.label}
              </div>
              <h2 className="text-xl font-bold leading-tight text-foreground break-words">
                {alarm.title}
              </h2>
            </div>
          </div>

          {alarm.body && (
            <div className="whitespace-pre-wrap text-sm text-foreground/90 leading-relaxed pl-1 max-h-[50vh] overflow-y-auto">
              {alarm.body}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            <Button onClick={handleDismiss} disabled={busy} className="gap-2">
              <X className="w-4 h-4" />
              {busy ? 'Закрываю…' : 'Понятно'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
